import ArticleLayout from '../../components/ArticleLayout';

export default function RadiatorTermostatIndstilling() {
  return (
    <ArticleLayout
      title="Hvad betyder tallene på termostaten i grader? Indstil rigtigt"
      metaTitle="Radiatortermostat indstilling i grader: Hvad betyder tallene"
      metaDescription="Se hvad tallene 1, 2, 3, 4 og 5 på radiatortermostaten svarer til i grader. Lær Danfoss' temperaturskala og få optimal varmeøkonomi."
      canonicalPath="/radiator-termostat-indstilling"
      introLead="Tallene på en radiatortermostat angiver ikke radiatorens overfladetemperatur, men den ønskede rumtemperatur i luften omkring termostaten."
      introAnswer="På standardtermostater (herunder Danfoss) svarer trin 3 til almindelig komfortabel stuetemperatur på ca. 20–21 °C. Hvert helt trin ændrer rumtemperaturen med ca. 3–4 °C, mens de små prikker eller streger mellem tallene svarer til ca. 1 °C pr. mærke."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Den optimale indstilling i boligen' },
        { id: 'temperaturskala', title: '2. Termostatskalaen i grader celcius' },
        { id: 'ens-indstilling', title: '3. Reglen om ens indstilling i samme rum' },
        { id: 'fejl-trin5', title: '4. Den klassiske fejl: Trin 5 varmer ikke hurtigere' },
        { id: 'faq', title: '5. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: Sådan indstiller du termostaterne fornuftigt"
      checklist={[
        {
          title: 'Stil opholdsrum og stuer på trin 3 (ca. 20–21 °C)',
          description: 'Trin 3 giver et sundt indeklima og forhindrer unødigt energispild i de rum, hvor familien opholder sig mest.',
        },
        {
          title: 'Stil soveværelser og gæsteværelser på trin 2 (ca. 17–18 °C)',
          description: 'En lavere temperatur giver en bedre nattesøvn, men hold altid minimum 18 °C mod fugt og skimmelsvamp.',
        },
        {
          title: 'Indstil alle radiatorer i samme rum på det samme trin',
          description: 'To radiatorer på trin 3 afkøler vandet markant bedre end én slukket radiator og én på trin 5.',
        },
        {
          title: 'Lad være med at skrue op og ned hele tiden',
          description: 'En termostat er selvregulerende og lukker automatisk ned, når solen skinner eller der laves mad.',
        },
        {
          title: 'Hold døre lukkede mellem rum med forskellig temperatur',
          description: 'Luk døren til kølige soveværelser, så stuevarmen ikke suges derind og belaster stueradiatorerne unødigt.',
        },
      ]}
      sections={[
        {
          id: 'temperaturskala',
          title: 'Termostatskalaen i grader celsius (Danfoss standard)',
          content: (
            <>
              <p>
                Her er den officielle oversigt over, hvordan skalaen på Danfoss og de fleste gængse mekaniske radiatortermostater er kalibreret:
              </p>
              <div className="overflow-x-auto my-4">
                <table className="w-full text-left text-sm border border-[#E6DED5]">
                  <thead className="bg-[#F3E6DC] text-[#221E1B]">
                    <tr>
                      <th className="p-3 font-semibold border-b border-[#E6DED5]">Mærke på skala</th>
                      <th className="p-3 font-semibold border-b border-[#E6DED5]">Målt rumtemperatur</th>
                      <th className="p-3 font-semibold border-b border-[#E6DED5]">Anbefalet anvendelse</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E6DED5]">
                    <tr>
                      <td className="p-3 font-mono font-bold">0 (eller lukket)</td>
                      <td className="p-3 font-mono">Ventil lukket helt</td>
                      <td className="p-3">Ved længerevarende udluftning med gennemtræk</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono font-bold">✱ (stjerne)</td>
                      <td className="p-3 font-mono">ca. 7–8 °C</td>
                      <td className="p-3">Frostsikring i ubeboede rum og bryggers</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono font-bold">Trin 1</td>
                      <td className="p-3 font-mono">ca. 12–14 °C</td>
                      <td className="p-3">Kælder, depotrum og uopvarmede gange</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono font-bold">Trin 2</td>
                      <td className="p-3 font-mono">ca. 16–18 °C</td>
                      <td className="p-3">Soveværelse, entré og kølige bryggersrum</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono font-bold text-[#B5531E]">Trin 3</td>
                      <td className="p-3 font-mono font-bold text-[#B5531E]">ca. 20–21 °C</td>
                      <td className="p-3 font-medium">Stue, børneværelse, alrum og kontor</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono font-bold">Trin 4</td>
                      <td className="p-3 font-mono">ca. 23–24 °C</td>
                      <td className="p-3">Badeværelse og ekstra varmebehov</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono font-bold">Trin 5</td>
                      <td className="p-3 font-mono">ca. 26 °C</td>
                      <td className="p-3">Maksimal åbning (bør normalt undgås i dagligdagen)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-[#6B625B]">
                Mellem tallene sidder der typisk tre eller fire prikker. Hver prik svarer til ca. <span className="font-mono">1 °C</span>. Hvis trin 3 giver lige lovlig meget varme, kan du stille viseren på prikken lige under 3 for ca. 20 °C.
              </p>
            </>
          ),
        },
        {
          id: 'ens-indstilling',
          title: 'Hvorfor SKAL alle radiatorer i rummet stå på samme trin?',
          content: (
            <>
              <p>
                Det er en udbredt misforståelse at slukke den ene radiator i en stue og lade den anden stå på fuld kraft (trin 5).
              </p>
              <p>
                Når kun én radiator arbejder, må den trække store mængder skoldhedt vand igennem for at varme hele lokalet op. Vandet når ikke at afgive sin varme ordentligt og løber brandvarmt ud i returrøret. Det ødelægger afkølingen på fjernvarme og udløser strafgebyrer.
              </p>
              <p>
                Ved at stille begge radiatorer på trin 3 arbejder de sammen med en rolig, lav vandstrøm. Radiatorerne bliver lune i toppen og kolde i bunden, og varmen udnyttes maksimalt.
              </p>
            </>
          ),
        },
        {
          id: 'fejl-trin5',
          title: 'Den klassiske fejl: Trin 5 varmer ikke rummet hurtigere op',
          content: (
            <>
              <p>
                Kommer du hjem til en kold stue, hjælper det ingenting at skrue termostaten helt op på 5 i håb om hurtigere opvarmning.
              </p>
              <p>
                En termostat fungerer som en termostat i en ovn eller fartpilot i en bil: Ventilen åbner fuldstændigt, så længe rumtemperaturen er under det indstillede mål. Hvis du stiller den på trin 3 i et 15 °C koldt rum, åbner den 100 % for vandet – nøjagtig ligesom hvis du havde sat den på 5. Forskellen er blot, at på trin 5 glemmer man den, hvorefter rummet overophedes til 26 °C med stort energispild til følge.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvad hvis radiatoren er brandvarm, selvom den står på trin 1?',
        content: (
          <p>
            Hvis radiatoren fortsætter med at varme kraftigt på trin 1 eller stjernen, lukker ventilen ikke tæt. Årsagen er enten en fastgroet ventilstift, en defekt pakdåse eller en termostatbælg, der har tabt sit ekspansionstryk. Udskift først termostathovedet, og tilkald en VVS-installatør, hvis fejlen fortsætter.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Hvor meget sparer man ved at sænke termostaten én grad?',
          a: 'Ifølge energimålinger fra HOFOR og Energistyrelsen sparer du ca. 5 % af dit varmeforbrug for hver grad, du sænker den gennemsnitlige rumtemperatur i din bolig.',
        },
        {
          q: 'Må man slukke helt for radiatoren om vinteren?',
          a: 'Nej. Rumtemperaturen bør aldrig komme under 18 °C af hensyn til indeklima og risiko for skimmelsvamp i kolde ydervægge. Sæt termostaten på minimum trin 2.',
        },
        {
          q: 'Hvad betyder det, hvis der ikke er tal, men kun prikker på termostaten?',
          a: 'Nogle ældre eller minimalistiske termostater har kun prikker eller romertal (I, II, III). Princippet er det samme: midterpositionen (eller III) svarer til 20 °C.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-termostat', text: 'Radiatortermostater forklaret', desc: 'mekaniske følere, gas, væske og udskiftning' },
        { to: '/spar-paa-varmen', text: 'Sådan sparer du på varmen', desc: 'flere gode spareråd fra forsyningsselskaberne' },
        { to: '/radiator-ventil-sidder-fast', text: 'Ventilstiften sidder fast', desc: 'når radiatoren ikke reagerer på termostaten' },
        { to: '/radiator-kold-i-bunden', text: 'Radiator kold i bunden', desc: 'hvorfor korrekt afkøling kræver rigtig indstilling' },
      ]}
      sources={[
        { url: 'https://www.danfoss.com/da-dk/industries/buildings-residential/dhs/solutions-for-homeowners/saving-energy/', label: 'Gode råd til at spare på varmen | Danfoss' },
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/spar-paa-varme-og-vand-i-hjemmet', label: 'Normalt varmeforbrug i kWh (+ fjernvarme i MWh) | SparEnergi' },
      ]}
    />
  );
}
